/**
 * RoChoiceChips - 选项芯片组（单选 / 多选）Web 组件
 *
 * 设计：未选中 1px #e1e1e1、选中 1px 品牌红 #ea0029；高 56px、px 16 py 12、圆角 4px、文案 14/20、项间距 8px（对齐 Figma 5490:16502 / 5485:15103）
 *
 * - multiple：未设置或 false 为单选；multiple 为多选
 * - 选项：优先光 DOM <option>，否则 options 属性（JSON 数组）
 * - default-value：单选为单个 value 字符串；多选为逗号分隔，或 JSON 数组字符串如 ["a","b"]
 * - value 属性：单选为选中 value；多选为逗号分隔（若选项 value 含逗号请用 default-value / 编程方式设值）
 * - columns：可选，正整数表示每行固定展示几个芯片（CSS Grid）；设 columns 时以网格为准，与 flex 下的 equal-split 无关
 * - columns-mobile：可选；视口 ≤767px 时的列数（1～24）。不设则与 columns 相同
 * - equal-split：可选布尔；存在时 flex 行内均分（flex: 1 1 0）；不设则芯片靠左自然排版（flex: 0 1 auto）
 * - chip-min-width / chip-min-width-mobile：可选长度（如 169 或 169px）；默认桌面 169px、移动端（≤767px）140px
 *
 * 用法：
 *   <ro-choice-chips name="lang" required>
 *     <option value="cn">Chinese</option>
 *     <option value="en">English</option>
 *   </ro-choice-chips>
 *   <ro-choice-chips name="tags[]" multiple>...</ro-choice-chips>
 *   <ro-choice-chips name="lang" equal-split>...</ro-choice-chips>
 *   <ro-choice-chips name="lang" columns="4">...</ro-choice-chips>
 *   <ro-choice-chips name="lang" columns="4" columns-mobile="2">...</ro-choice-chips>
 * - 必填文案：error-message 或 window.RoFormLocale.required（见 snippets/ro-form-locale.liquid）
 */
class RoChoiceChips extends HTMLElement {
  static formAssociated = true;

  /** 合法列数为 1～24；空或非法则返回 null（使用默认 flex 布局） */
  static _normalizeColumns(raw) {
    if (raw == null || String(raw).trim() === "") return null;
    const n = parseInt(String(raw).trim(), 10);
    if (!Number.isFinite(n) || n < 1) return null;
    return Math.min(n, 24);
  }

  /** 解析 chip 最小宽度为合法 CSS 长度；非法返回 null */
  static _normalizeSizeCss(raw) {
    if (raw == null || String(raw).trim() === "") return null;
    const s = String(raw).trim();
    if (/^\d+(\.\d+)?$/.test(s)) return `${s}px`;
    if (/^\d+(\.\d+)?(px|em|rem|%|ch|vw|vh)$/i.test(s)) return s;
    return null;
  }

  static get observedAttributes() {
    return [
      "value",
      "disabled",
      "required",
      "multiple",
      "options",
      "default-value",
      "error-message",
      "validate-on-change",
      "columns",
      "columns-mobile",
      "equal-split",
      "chip-min-width",
      "chip-min-width-mobile",
    ];
  }

  constructor() {
    super();
    this.attachShadow({ mode: "open" });
    this._internals = this.attachInternals();
    this._options = [];
    this._singleValue = "";
    /** @type {Set<string>} */
    this._multiSet = new Set();
    this._isError = false;
    this._errorMessage = "";
    this._boundChipKeydown = this._onChipKeydown.bind(this);
    this._mo = null;
    this._render();
  }

  _render() {
    const groupId = `ro-choice-chips-group-${Math.random().toString(36).slice(2, 9)}`;

    this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: inline-flex;
          flex-direction: column;
          width: 100%;
          vertical-align: top;
          --ro-chip-min-width: 169px;
          --ro-chip-min-width-mobile: 140px;
        }
        .chips {
          display: flex;
          flex-wrap: wrap;
          align-items: stretch;
          gap: 8px;
          width: 100%;
        }
        .chip {
          flex: 0 1 auto;
          min-width: var(--ro-chip-min-width);
          box-sizing: border-box;
          min-height: 56px;
          margin: 0;
          padding: 12px 16px;
          cursor: pointer;
          font-weight: 400;
          font-size: 14px;
          line-height: 20px;
          text-align: center;
          color: var(--main-color, #161616);
          background: var(--bg-white-color, #fff);
          border: 1px solid var(--border-color, #e1e1e1);
          border-radius: 4px;
          transition: border-color 0.2s ease, background 0.15s ease;
        }
        .chips[data-equal-split] .chip {
          flex: 1 1 0;
        }
        @media (max-width: 767px) {
          .chip {
            min-width: var(--ro-chip-min-width-mobile);
          }
        }
        .chip:hover:not(:disabled) {
          border-color: var(--main-color, #161616);
        }
        .chip.selected {
          border-color: var(--brand-color, #ea0029);
        }
        .chip:focus-visible {
          outline: 2px solid var(--brand-color, #ea0029);
          outline-offset: 2px;
        }
        :host([disabled]) .chip {
          cursor: not-allowed;
          opacity: 0.55;
          background: var(--ro-select-disabled-bg, rgba(225, 225, 225, 0.4));
        }
        .chips[data-columns] {
          display: grid;
          grid-template-columns: repeat(var(--ro-choice-columns, 1), minmax(0, 1fr));
        }
        @media (max-width: 767px) {
          .chips[data-columns] {
            grid-template-columns: repeat(
              var(--ro-choice-columns-mobile, var(--ro-choice-columns, 1)),
              minmax(0, 1fr)
            );
          }
        }
        .chips[data-columns] .chip {
          flex: none;
          min-width: 0;
        }
        .error-message {
          display: none;
          margin-top: 8px;
          padding: 0 16px;
          color: var(--error-color, #d93840);
          font-weight: 400;
          font-size: var(--error-font-size, 12px);
          line-height: var(--error-line-height, 16px);
        }
        :host([error]) .error-message { display: block; }
      </style>
      <div class="chips" part="chips" id="${groupId}" role="radiogroup"></div>
      <div class="error-message" part="error" aria-live="polite"></div>
    `;

    this._chipsContainer = this.shadowRoot.querySelector(".chips");
    this._errorEl = this.shadowRoot.querySelector(".error-message");
  }

  /** @returns {boolean} */
  _isMultiple() {
    return this.hasAttribute("multiple");
  }

  /** 有 columns 时为网格等分列（桌面/移动列数由 :host 上 CSS 变量控制）；否则恢复 flex */
  _syncColumnsLayout() {
    if (!this._chipsContainer) return;
    const n = RoChoiceChips._normalizeColumns(this.getAttribute("columns"));
    if (n == null) {
      this._chipsContainer.removeAttribute("data-columns");
      this.style.removeProperty("--ro-choice-columns");
      this.style.removeProperty("--ro-choice-columns-mobile");
      return;
    }
    this._chipsContainer.setAttribute("data-columns", String(n));
    this.style.setProperty("--ro-choice-columns", String(n));
    const nm = RoChoiceChips._normalizeColumns(this.getAttribute("columns-mobile"));
    if (nm != null) this.style.setProperty("--ro-choice-columns-mobile", String(nm));
    else this.style.removeProperty("--ro-choice-columns-mobile");
  }

  /** 解析 equal-split：无属性或显式 false/0 为关，其余为开 */
  _isEqualSplitOn() {
    if (!this.hasAttribute("equal-split")) return false;
    const v = (this.getAttribute("equal-split") || "").trim().toLowerCase();
    if (v === "false" || v === "0") return false;
    return true;
  }

  /** flex 下行内均分标记 + :host 上最小宽度变量（columns 网格下不设置 equal-split） */
  _syncChipLayoutExtras() {
    if (!this._chipsContainer) return;
    const w = RoChoiceChips._normalizeSizeCss(this.getAttribute("chip-min-width"));
    const wm = RoChoiceChips._normalizeSizeCss(this.getAttribute("chip-min-width-mobile"));
    if (w) this.style.setProperty("--ro-chip-min-width", w);
    else this.style.removeProperty("--ro-chip-min-width");
    if (wm) this.style.setProperty("--ro-chip-min-width-mobile", wm);
    else this.style.removeProperty("--ro-chip-min-width-mobile");

    const grid = RoChoiceChips._normalizeColumns(this.getAttribute("columns")) != null;
    if (grid) {
      this._chipsContainer.removeAttribute("data-equal-split");
    } else if (this._isEqualSplitOn()) {
      this._chipsContainer.setAttribute("data-equal-split", "");
    } else {
      this._chipsContainer.removeAttribute("data-equal-split");
    }
  }

  connectedCallback() {
    this._ensureOptionsHiddenStyle();
    this._buildOptions();
    this._hydrateValueFromAttributes();
    this._syncModeA11y();
    this._syncFormValue();
    this._renderChips();
    this._syncColumnsLayout();
    this._syncChipLayoutExtras();
    if (!this._mo) {
      this._mo = new MutationObserver(() => {
        this._buildOptions();
        this._renderChips();
      });
      this._mo.observe(this, { childList: true, subtree: true, attributes: true, attributeFilter: ["value", "disabled"] });
    }
  }

  disconnectedCallback() {
    if (this._mo) {
      this._mo.disconnect();
      this._mo = null;
    }
  }

  _ensureOptionsHiddenStyle() {
    if (this.querySelector("style[data-ro-choice-chips-options]")) return;
    const s = document.createElement("style");
    s.setAttribute("data-ro-choice-chips-options", "");
    s.textContent = ":host > option { display: none !important; }";
    this.insertBefore(s, this.firstChild);
  }

  _buildOptions() {
    const fromChildren = this.querySelectorAll("option");
    if (fromChildren.length) {
      this._options = Array.from(fromChildren).map((o) => ({
        value: o.getAttribute("value") ?? o.textContent.trim(),
        label: (o.textContent || "").trim() || o.getAttribute("value") || "",
        disabled: o.disabled || o.hasAttribute("disabled"),
      }));
      return;
    }
    const optsAttr = this.getAttribute("options");
    if (optsAttr) {
      try {
        const arr = JSON.parse(optsAttr);
        this._options = Array.isArray(arr)
          ? arr.map((o) => ({
              value: String(o.value ?? o.id ?? ""),
              label: String(o.label ?? o.name ?? o.text ?? ""),
              disabled: !!o.disabled,
            }))
          : [];
      } catch (_) {
        this._options = [];
      }
      return;
    }
    this._options = [];
  }

  /** 从 value / default-value 拉齐内部状态（attributeChanged 与 connected 共用逻辑的一部分） */
  _hydrateValueFromAttributes() {
    const raw = this.getAttribute("value");
    if (raw != null && raw !== "") {
      this._applyExternalValueString(raw);
      return;
    }
    const def = this.getAttribute("default-value");
    if (def != null && def !== "") {
      this._applyExternalValueString(def);
    } else if (this._isMultiple()) {
      this._multiSet.clear();
    } else {
      this._singleValue = "";
    }
  }

  /** @param {string} raw */
  _applyExternalValueString(raw) {
    if (this._isMultiple()) {
      this._multiSet.clear();
      const parts = this._parseMultiValueString(raw);
      parts.forEach((p) => this._multiSet.add(p));
    } else {
      this._singleValue = raw;
    }
  }

  /** @param {string} raw @returns {string[]} */
  _parseMultiValueString(raw) {
    const t = String(raw).trim();
    if (t.startsWith("[") && t.endsWith("]")) {
      try {
        const arr = JSON.parse(t);
        return Array.isArray(arr) ? arr.map((x) => String(x)) : [];
      } catch (_) {
        return [];
      }
    }
    return t.split(",").map((s) => s.trim()).filter(Boolean);
  }

  _syncModeA11y() {
    const multi = this._isMultiple();
    this._chipsContainer.setAttribute("role", multi ? "group" : "radiogroup");
    if (multi) this._chipsContainer.setAttribute("aria-multiselectable", "true");
    else this._chipsContainer.removeAttribute("aria-multiselectable");
  }

  _syncValueAttributeFromState() {
    if (this._isMultiple()) {
      const s = Array.from(this._multiSet).join(",");
      if (s) this.setAttribute("value", s);
      else this.removeAttribute("value");
    } else {
      if (this._singleValue) this.setAttribute("value", this._singleValue);
      else this.removeAttribute("value");
    }
  }

  _syncFormValue() {
    const name = this.getAttribute("name");
    if (this._isMultiple()) {
      if (!this._multiSet.size || !name) {
        this._internals.setFormValue(null);
        return;
      }
      const fd = new FormData();
      for (const v of this._multiSet) fd.append(name, v);
      this._internals.setFormValue(fd);
      return;
    }
    this._internals.setFormValue(this._singleValue ? this._singleValue : null);
  }

  _renderChips() {
    const multi = this._isMultiple();
    this._chipsContainer.innerHTML = "";

    this._options.forEach((opt) => {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "chip";
      btn.part = "chip";
      btn.textContent = opt.label;
      btn.dataset.value = opt.value;
      const selected = multi ? this._multiSet.has(opt.value) : this._singleValue === opt.value;
      if (selected) btn.classList.add("selected");
      if (opt.disabled || this.hasAttribute("disabled")) btn.disabled = true;

      btn.setAttribute("role", multi ? "checkbox" : "radio");
      btn.setAttribute("aria-checked", selected ? "true" : "false");

      const labelledby = this.getAttribute("aria-labelledby");
      if (labelledby) btn.setAttribute("aria-labelledby", labelledby);

      btn.addEventListener("click", () => this._onChipClick(opt.value, opt.disabled));
      btn.addEventListener("keydown", this._boundChipKeydown);

      this._chipsContainer.appendChild(btn);
    });

    this._syncRovingTabindex();
  }

  _syncRovingTabindex() {
    const buttons = this._getEnabledChipButtons();
    if (!buttons.length) return;
    const multi = this._isMultiple();
    let focusIdx = 0;
    if (!multi && this._singleValue) {
      const idx = buttons.findIndex((b) => b.dataset.value === this._singleValue);
      if (idx >= 0) focusIdx = idx;
    }
    buttons.forEach((b, i) => {
      b.setAttribute("tabindex", i === focusIdx ? "0" : "-1");
    });
  }

  _getEnabledChipButtons() {
    return Array.from(this._chipsContainer.querySelectorAll(".chip:not([disabled])"));
  }

  _onChipClick(value, disabled) {
    if (disabled || this.hasAttribute("disabled")) return;
    const multi = this._isMultiple();
    if (multi) {
      if (this._multiSet.has(value)) this._multiSet.delete(value);
      else this._multiSet.add(value);
    } else {
      this._singleValue = value;
    }
    this._commitChange();
    if (this.hasAttribute("validate-on-change")) this.validate();
  }

  _commitChange() {
    this._syncValueAttributeFromState();
    this._syncFormValue();
    this._renderChips();
    const detail = this._isMultiple()
      ? { value: Array.from(this._multiSet), values: Array.from(this._multiSet) }
      : { value: this._singleValue, values: this._singleValue ? [this._singleValue] : [] };
    this.dispatchEvent(new CustomEvent("change", { bubbles: true, detail }));
  }

  /** @param {KeyboardEvent} e */
  _onChipKeydown(e) {
    if (this.hasAttribute("disabled")) return;
    const buttons = this._getEnabledChipButtons();
    if (!buttons.length) return;
    const i = buttons.indexOf(e.currentTarget);
    if (i < 0) return;

    const multi = this._isMultiple();

    if (multi) {
      if (e.key === " " || e.key === "Enter") {
        e.preventDefault();
        const v = e.currentTarget.dataset.value;
        this._onChipClick(v, false);
      }
      return;
    }

    let next = i;
    if (e.key === "ArrowRight" || e.key === "ArrowDown") {
      e.preventDefault();
      next = Math.min(buttons.length - 1, i + 1);
    } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
      e.preventDefault();
      next = Math.max(0, i - 1);
    } else if (e.key === " " || e.key === "Enter") {
      e.preventDefault();
      const v = e.currentTarget.dataset.value;
      this._onChipClick(v, false);
      return;
    } else {
      return;
    }

    if (next !== i) {
      buttons[next].focus();
      buttons.forEach((b, j) => b.setAttribute("tabindex", j === next ? "0" : "-1"));
    }
  }

  attributeChangedCallback(name, oldVal, newVal) {
    if (oldVal === newVal) return;
    switch (name) {
      case "multiple":
        this._syncModeA11y();
        this._hydrateValueFromAttributes();
        this._syncFormValue();
        this._renderChips();
        break;
      case "value":
        if (newVal != null && newVal !== "") {
          this._applyExternalValueString(newVal);
        } else if (this._isMultiple()) {
          this._multiSet.clear();
        } else {
          this._singleValue = "";
        }
        this._syncFormValue();
        this._renderChips();
        break;
      case "options":
        this._buildOptions();
        this._renderChips();
        break;
      case "disabled":
        this._renderChips();
        break;
      case "columns":
      case "columns-mobile":
        this._syncColumnsLayout();
        this._syncChipLayoutExtras();
        break;
      case "equal-split":
      case "chip-min-width":
      case "chip-min-width-mobile":
        this._syncChipLayoutExtras();
        break;
      case "error-message":
        if (this._isError) this._errorEl.textContent = newVal || this._errorMessage;
        break;
      default:
        break;
    }
  }

  validate() {
    if (this.hasAttribute("disabled")) return { valid: true, message: "" };
    let errorMessage = "";
    if (this.hasAttribute("required")) {
      const empty = this._isMultiple() ? this._multiSet.size === 0 : !this._singleValue;
      if (empty) {
        errorMessage =
          this.getAttribute("error-message") ||
          window.RoFormLocale?.required ||
          "This field is required";
      }
    }
    const valid = !errorMessage;
    this._isError = !valid;
    this._errorMessage = errorMessage;
    if (this._isError) {
      this.setAttribute("error", "");
      this._errorEl.textContent = this.getAttribute("error-message") || errorMessage;
    } else {
      this.removeAttribute("error");
      this._errorEl.textContent = "";
    }
    this.dispatchEvent(
      new CustomEvent("validate", {
        bubbles: true,
        detail: {
          valid,
          message: errorMessage,
          value: this._isMultiple() ? Array.from(this._multiSet) : this._singleValue,
        },
      })
    );
    if (!valid) {
      this.dispatchEvent(
        new CustomEvent("error", {
          bubbles: true,
          detail: { message: errorMessage, value: this._isMultiple() ? Array.from(this._multiSet) : this._singleValue },
        })
      );
    }
    return { valid, message: errorMessage };
  }

  reset() {
    const def = this.getAttribute("default-value");
    if (this._isMultiple()) {
      this._multiSet.clear();
      if (def != null && def !== "") {
        this._parseMultiValueString(def).forEach((p) => this._multiSet.add(p));
      }
    } else if (def != null && def !== "") {
      this._singleValue = def;
    } else {
      this._singleValue = "";
    }
    this._syncValueAttributeFromState();
    this._syncFormValue();
    this._setError(false, "");
    this._renderChips();
  }

  clearError() {
    this._setError(false, "");
  }

  focus() {
    const first = this._getEnabledChipButtons()[0];
    if (first) first.focus();
  }

  _setError(isError, message) {
    this._isError = isError;
    this._errorMessage = message;
    if (isError) {
      this.setAttribute("error", "");
      this._errorEl.textContent = this.getAttribute("error-message") || message;
    } else {
      this.removeAttribute("error");
      this._errorEl.textContent = "";
    }
  }

  get value() {
    return this._isMultiple() ? Array.from(this._multiSet).join(",") : this._singleValue;
  }

  set value(val) {
    const s = val != null ? String(val) : "";
    if (this._isMultiple()) {
      this._multiSet.clear();
      this._parseMultiValueString(s).forEach((p) => this._multiSet.add(p));
    } else {
      this._singleValue = s;
    }
    this._syncValueAttributeFromState();
    this._syncFormValue();
    this._renderChips();
  }

  get values() {
    return this._isMultiple() ? Array.from(this._multiSet) : this._singleValue ? [this._singleValue] : [];
  }

  get disabled() {
    return this.hasAttribute("disabled");
  }
  set disabled(val) {
    if (val) this.setAttribute("disabled", "");
    else this.removeAttribute("disabled");
  }

  get required() {
    return this.hasAttribute("required");
  }
  set required(val) {
    if (val) this.setAttribute("required", "");
    else this.removeAttribute("required");
  }

  /** 每行芯片个数；未设置或非合法正整数时为 null（默认 flex 布局） */
  get columns() {
    return RoChoiceChips._normalizeColumns(this.getAttribute("columns"));
  }
  set columns(val) {
    if (val == null || val === "") this.removeAttribute("columns");
    else this.setAttribute("columns", String(val));
  }

  /** 移动端（≤767px）列数；无 columns 时为 null；未设置 columns-mobile 时为 null（CSS 回落为与桌面相同） */
  get columnsMobile() {
    if (RoChoiceChips._normalizeColumns(this.getAttribute("columns")) == null) return null;
    return RoChoiceChips._normalizeColumns(this.getAttribute("columns-mobile"));
  }
  set columnsMobile(val) {
    if (val == null || val === "") this.removeAttribute("columns-mobile");
    else this.setAttribute("columns-mobile", String(val));
  }

  formResetCallback() {
    this.reset();
  }

  formDisabledCallback(disabled) {
    this.disabled = disabled;
    this._renderChips();
  }
}

if (!customElements.get("ro-choice-chips")) {
  customElements.define("ro-choice-chips", RoChoiceChips);
}
